import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Building2,
  ChevronDown,
  Clock3,
  GitCompareArrows,
  List,
  Network,
  PanelLeft,
  Plus,
  RotateCcw,
  Search,
  Sparkles,
  Users,
} from 'lucide-react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import type { Viewport } from '@xyflow/react';
import Header from '../components/Header';
import { companiesApi, type Company, type TeamMember } from '../services/api';
import { useOrganizationWorkspace } from '../organization/useOrganizationWorkspace';
import { organizationApi } from '../organization/api';
import {
  compareStructures,
  selectedRoleIds,
  visibleRoleIds,
  type EntitySelection,
} from '../organization/graph';
import type { OrganizationSnapshot, OrganizationVersionDetail } from '../organization/model';
import { isProposedRole } from '../organization/model';
import ReportingCanvas, { initials } from '../organization/ReportingCanvas';
import WorkbenchPanel from '../organization/WorkbenchPanel';
import StructureOutline from '../organization/StructureOutline';
import EntityInspector from '../organization/EntityInspector';
import HistoryPanel, { versionDate } from '../organization/HistoryPanel';
import PeoplePanel from '../organization/PeoplePanel';
import RoleQuickEditor from '../organization/RoleQuickEditor';
import StructureWizard from '../organization/StructureWizard';
import './CompanyWorkPages.css';
import '../organization/OrganizationWorkspace.css';

type ViewState =
  | { mode: 'current' }
  | { mode: 'history'; version: OrganizationVersionDetail }
  | { mode: 'compare'; before: OrganizationVersionDetail; after: OrganizationVersionDetail };
type Panel = 'structure' | 'history' | 'people' | null;

export default function OrganizationalPage() {
  const { companyId = '' } = useParams<{ companyId: string }>();
  return <OrganizationWorkspace key={companyId} companyId={companyId} />;
}

function OrganizationWorkspace({ companyId }: { companyId: string }) {
  const data = useOrganizationWorkspace(companyId);
  const [company, setCompany] = useState<Company | null>(null);
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [membersError, setMembersError] = useState('');
  const [companyError, setCompanyError] = useState('');
  const [panel, setPanel] = useState<Panel>(() => (window.innerWidth > 1300 ? 'structure' : null));
  const [selection, setSelection] = useState<EntitySelection | null>(null);
  const [view, setView] = useState<ViewState>({ mode: 'current' });
  const [viewSide, setViewSide] = useState<'before' | 'after'>('after');
  const [viewMode, setViewMode] = useState<'chart' | 'list'>('chart');
  const [search, setSearch] = useState('');
  const [group, setGroup] = useState('');
  const [wizardOpen, setWizardOpen] = useState(false);
  const [quickEdit, setQuickEdit] = useState<{
    roleId: string;
    mode: 'people' | 'reporting';
  } | null>(null);
  const [versionLoading, setVersionLoading] = useState(false);
  const [versionError, setVersionError] = useState('');
  const [compact, setCompact] = useState(window.innerWidth < 1100);
  const [compareNarrow, setCompareNarrow] = useState(window.innerWidth < 1400);
  const [sharedViewport, setSharedViewport] = useState<Viewport | undefined>();
  const currentViewport = useRef<Viewport>();
  const currentSelection = useRef<EntitySelection | null>(null);
  const dirty = useRef(false);
  const viewRequest = useRef(0);
  const versionRetry = useRef<(() => void) | null>(null);
  const workbench = useRef<HTMLDivElement>(null);
  const canvasColumn = useRef<HTMLDivElement>(null);
  const lifetime = useRef(true);
  const onDirty = useCallback((value: boolean) => {
    dirty.current = value;
  }, []);
  useEffect(() => {
    lifetime.current = true;
    return () => {
      lifetime.current = false;
      viewRequest.current++;
    };
  }, []);
  const refreshMembers = useCallback(async () => {
    try {
      const response = await companiesApi.getTeamMembers(companyId);
      if (!lifetime.current) return;
      if (!response.success || !response.data)
        throw new Error(response.error || 'Company people are unavailable.');
      setMembers(response.data);
      setMembersError('');
    } catch (failure) {
      if (lifetime.current)
        setMembersError(
          failure instanceof Error ? failure.message : 'Company people are unavailable.'
        );
    }
  }, [companyId]);
  const refreshCompany = useCallback(async () => {
    try {
      const response = await companiesApi.get(companyId);
      if (!lifetime.current) return;
      if (!response.success || !response.data)
        throw new Error(response.error || 'Company information is unavailable.');
      setCompany(response.data);
      setCompanyError('');
    } catch (error) {
      if (lifetime.current)
        setCompanyError(
          error instanceof Error ? error.message : 'Company information is unavailable.'
        );
    }
  }, [companyId]);
  useEffect(() => {
    void refreshCompany();
    void refreshMembers();
  }, [refreshCompany, refreshMembers]);
  useEffect(() => {
    if (!workbench.current || !canvasColumn.current) return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.target === workbench.current) setCompact(entry.contentRect.width < 1000);
        if (entry.target === canvasColumn.current) setCompareNarrow(entry.contentRect.width < 1050);
      }
    });
    observer.observe(workbench.current);
    observer.observe(canvasColumn.current);
    return () => observer.disconnect();
  }, [data.loading]);
  const canEdit =
    company?.capabilities?.editOrganisation ?? ['owner', 'admin'].includes(company?.role || '');
  const canInvite =
    company?.capabilities?.manageMembers ?? ['owner', 'admin'].includes(company?.role || '');
  const discard = useCallback(
    () => !dirty.current || window.confirm('Discard unsaved changes to this item?'),
    []
  );
  const select = useCallback(
    (next: EntitySelection) => {
      if (!discard()) return;
      dirty.current = false;
      setSelection(next);
      if (next.id && next.kind !== 'position') setGroup(`${next.kind}:${next.id}`);
      if (compact) setPanel(null);
    },
    [discard, compact]
  );
  const closeInspector = useCallback(() => {
    if (discard()) {
      dirty.current = false;
      setSelection(null);
    }
  }, [discard]);
  useEffect(() => {
    const resized = () => {
      currentViewport.current = undefined;
    };
    window.addEventListener('resize', resized);
    return () => window.removeEventListener('resize', resized);
  }, []);
  const current = data.workspace;
  const currentVersion = useMemo<OrganizationVersionDetail | null>(
    () =>
      current
        ? {
            id: current.versionId || 'current',
            revision: current.revision,
            label: 'Current structure',
            createdAt: current.snapshot.capturedAt,
            actorName: null,
            summary: 'Current structure',
            snapshot: current.snapshot,
          }
        : null,
    [current]
  );
  const snapshot =
    view.mode === 'current'
      ? current?.snapshot
      : view.mode === 'history'
        ? view.version.snapshot
        : viewSide === 'before'
          ? view.before.snapshot
          : view.after.snapshot;
  const changes = useMemo(
    () =>
      view.mode === 'compare' ? compareStructures(view.before.snapshot, view.after.snapshot) : [],
    [view]
  );
  const openVersion = async (versionId: string, compare = false) => {
    if (!currentVersion || !discard()) return;
    const request = ++viewRequest.current;
    versionRetry.current = () => {
      void openVersion(versionId, compare);
    };
    setVersionLoading(true);
    setVersionError('');
    try {
      const loaded = await organizationApi.version(companyId, versionId);
      if (request !== viewRequest.current) return;
      if (view.mode === 'current') currentSelection.current = selection;
      dirty.current = false;
      setSelection(null);
      setSearch('');
      setGroup('');
      setSharedViewport(undefined);
      setViewSide('after');
      setView(
        compare
          ? { mode: 'compare', before: loaded, after: currentVersion }
          : { mode: 'history', version: loaded }
      );
      if (compact) setPanel(null);
    } catch (error) {
      if (request === viewRequest.current)
        setVersionError(error instanceof Error ? error.message : 'Could not load this version.');
    } finally {
      if (request === viewRequest.current) setVersionLoading(false);
    }
  };
  const returnCurrent = () => {
    viewRequest.current++;
    setVersionLoading(false);
    setVersionError('');
    versionRetry.current = null;
    setView({ mode: 'current' });
    setSelection(currentSelection.current);
    setSearch('');
    setGroup('');
    setSharedViewport(undefined);
  };
  const changeCompareVersion = async (side: 'before' | 'after', id: string) => {
    if (view.mode !== 'compare' || !currentVersion) return;
    const request = ++viewRequest.current;
    versionRetry.current = () => {
      void changeCompareVersion(side, id);
    };
    setVersionLoading(true);
    setVersionError('');
    try {
      const version =
        id === 'current' ? currentVersion : await organizationApi.version(companyId, id);
      if (request !== viewRequest.current) return;
      setView({ ...view, [side]: version });
      setSelection(null);
      setSharedViewport(undefined);
    } catch (error) {
      if (request === viewRequest.current)
        setVersionError(error instanceof Error ? error.message : 'Could not load this version.');
    } finally {
      if (request === viewRequest.current) setVersionLoading(false);
    }
  };
  const togglePanel = (next: Panel) => {
    if (compact && selection && !discard()) return;
    if (compact) setSelection(null);
    setPanel(panel === next ? null : next);
  };
  const panelTitle =
    panel === 'structure' ? 'Structure' : panel === 'history' ? 'Version history' : 'People';

  return (
    <div className="company-work-page company-workspace-screen org-workspace">
      <Header
        title="People & structure"
        subtitle="See how your people connect. Shape the organisation as it grows."
        company={company ? { name: company.name, logo: company.logo } : undefined}
        actions={
          <div className="org-header-actions">
            <button
              type="button"
              className="org-button org-button-secondary"
              onClick={() => togglePanel('people')}
              aria-pressed={panel === 'people'}
            >
              <Users size={15} />
              People
              {members.length > 0 && (
                <span className="org-inline-count">
                  {members.filter((m) => m.status === 'active').length}
                </span>
              )}
            </button>
            {canEdit && view.mode === 'current' && (
              <>
                <button
                  type="button"
                  className="org-button org-button-secondary"
                  disabled={!current}
                  onClick={() => {
                    if (discard()) {
                      setSelection(null);
                      setWizardOpen(true);
                    }
                  }}
                >
                  <Sparkles size={15} />
                  Guided setup
                </button>
                <DropdownMenu.Root>
                  <DropdownMenu.Trigger asChild>
                    <button
                      type="button"
                      className="org-button org-button-primary"
                      disabled={!current}
                    >
                      <Plus size={16} />
                      Add
                      <ChevronDown size={13} />
                    </button>
                  </DropdownMenu.Trigger>
                  <DropdownMenu.Portal>
                    <DropdownMenu.Content className="org-dropdown" sideOffset={6}>
                      <DropdownMenu.Item onSelect={() => select({ kind: 'position' })}>
                        <Network size={15} />
                        Reporting role
                      </DropdownMenu.Item>
                      <DropdownMenu.Item onSelect={() => select({ kind: 'division' })}>
                        <Building2 size={15} />
                        Division
                      </DropdownMenu.Item>
                      <DropdownMenu.Item onSelect={() => select({ kind: 'team' })}>
                        <Users size={15} />
                        Team
                      </DropdownMenu.Item>
                    </DropdownMenu.Content>
                  </DropdownMenu.Portal>
                </DropdownMenu.Root>
              </>
            )}
          </div>
        }
      />
      <div className="org-toolbar">
        <div className="org-toolbar-left">
          <button
            type="button"
            className={`org-button org-button-quiet ${panel === 'structure' ? 'is-active' : ''}`}
            aria-label="Structure"
            aria-pressed={panel === 'structure'}
            onClick={() => togglePanel('structure')}
          >
            <PanelLeft size={15} />
            <span>Structure</span>
          </button>
          <label className="org-search">
            <Search size={15} />
            <span className="org-sr-only">Search roles, people or groups</span>
            <input
              type="search"
              placeholder="Find a role, person or group…"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </label>
          <label className="org-group-filter">
            <span className="org-sr-only">Filter organisational group</span>
            <select value={group} onChange={(event) => setGroup(event.target.value)}>
              <option value="">All groups</option>
              {snapshot?.divisions.map((d) => (
                <option value={`division:${d.id}`} key={d.id}>
                  Division: {d.name}
                </option>
              ))}
              {snapshot?.teams.map((t) => (
                <option value={`team:${t.id}`} key={t.id}>
                  Team: {t.name}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="org-toolbar-right">
          {view.mode === 'current' && (
            <span className="org-current-label">
              Current structure{current && <small>v{current.revision}</small>}
            </span>
          )}
          <button
            type="button"
            className={`org-button org-button-quiet ${panel === 'history' ? 'is-active' : ''}`}
            onClick={() => togglePanel('history')}
            aria-pressed={panel === 'history'}
          >
            <Clock3 size={15} />
            <span>History</span>
          </button>
          {view.mode !== 'compare' && (
            <div className="org-view-toggle" aria-label="Structure presentation">
              <button
                type="button"
                aria-pressed={viewMode === 'chart'}
                onClick={() => setViewMode('chart')}
              >
                <Network size={14} />
                <span>Chart</span>
              </button>
              <button
                type="button"
                aria-pressed={viewMode === 'list'}
                onClick={() => setViewMode('list')}
              >
                <List size={14} />
                <span>List</span>
              </button>
            </div>
          )}
        </div>
      </div>
      {view.mode !== 'current' && (
        <div className="org-history-banner">
          <Clock3 size={15} />
          <div>
            <strong>
              {view.mode === 'history'
                ? `${view.version.label || `Version ${view.version.revision}`} · ${versionDate(view.version.createdAt)}`
                : 'Compare structures'}
            </strong>
            <span>Saved structures are read-only.</span>
          </div>
          {view.mode === 'history' && (
            <button
              type="button"
              className="org-text-button"
              onClick={() => void openVersion(view.version.id, true)}
            >
              <GitCompareArrows size={14} />
              Compare with current
            </button>
          )}
          <button type="button" className="org-button org-button-secondary" onClick={returnCurrent}>
            <RotateCcw size={14} />
            Back to current
          </button>
        </div>
      )}
      {(versionError || (data.error && current) || companyError) && (
        <div className="org-page-error" role="alert">
          {versionError || data.error || companyError}
          <button
            type="button"
            className="org-text-button"
            onClick={() => {
              if (companyError) void refreshCompany();
              if (data.error) void data.refresh();
              if (versionError) versionRetry.current?.();
            }}
          >
            Retry
          </button>
        </div>
      )}
      <div
        className={`org-workbench ${view.mode === 'compare' ? 'org-workbench-compare' : ''}`}
        ref={workbench}
      >
        {panel && snapshot && (
          <WorkbenchPanel
            title={panelTitle}
            side="left"
            compact={compact}
            onClose={() => setPanel(null)}
          >
            {panel === 'structure' ? (
              <StructureOutline
                snapshot={snapshot}
                selection={selection}
                onSelect={select}
                canEdit={canEdit && view.mode === 'current'}
              />
            ) : panel === 'history' ? (
              <HistoryPanel
                companyId={companyId}
                versions={data.versions}
                currentId={current?.versionId || null}
                viewingId={view.mode === 'history' ? view.version.id : undefined}
                canEdit={canEdit}
                onView={(version) => void openVersion(version.id)}
                onCompare={(version) => void openVersion(version.id, true)}
                onUpdated={() => void data.refresh()}
                error={data.historyError}
                hasMore={data.hasMore}
                loadingMore={data.loadingMore}
                onMore={() => void data.loadMore()}
              />
            ) : (
              <PeoplePanel
                companyId={companyId}
                members={members}
                snapshot={current!.snapshot}
                canInvite={canInvite && view.mode === 'current'}
                error={membersError}
                onRefresh={() => void refreshMembers()}
              />
            )}
          </WorkbenchPanel>
        )}
        <div className="org-canvas-column" ref={canvasColumn}>
          {data.loading || !snapshot ? (
            <div className="org-load-state">
              {data.error ? (
                <>
                  <Network size={28} />
                  <h2>Structure is unavailable</h2>
                  <p>{data.error}</p>
                  <button
                    type="button"
                    className="org-button org-button-primary"
                    onClick={() => void data.refresh()}
                  >
                    Try again
                  </button>
                </>
              ) : (
                <>
                  <span className="org-spinner" />
                  <p>Loading your organisation…</p>
                </>
              )}
            </div>
          ) : view.mode === 'compare' ? (
            <>
              <div className="org-compare-selectors">
                {(['before', 'after'] as const).map((side) => (
                  <label key={side}>
                    <span>{side === 'before' ? 'Before' : 'After'}</span>
                    <select
                      aria-label={`${side === 'before' ? 'Before' : 'After'} structure version`}
                      value={view[side].id === currentVersion?.id ? 'current' : view[side].id}
                      onChange={(event) => void changeCompareVersion(side, event.target.value)}
                      disabled={versionLoading}
                    >
                      <option value="current">Current structure · v{current?.revision}</option>
                      {!data.versions.some((v) => v.id === view[side].id) &&
                        view[side].id !== currentVersion?.id && (
                          <option value={view[side].id}>v{view[side].revision}</option>
                        )}
                      {data.versions
                        .filter((v) => v.id !== currentVersion?.id)
                        .map((v) => (
                          <option key={v.id} value={v.id}>
                            {v.label || `Version ${v.revision}`} · {versionDate(v.createdAt)}
                          </option>
                        ))}
                    </select>
                  </label>
                ))}
              </div>
              {compareNarrow && (
                <div className="org-compare-mobile-tabs">
                  <button
                    type="button"
                    aria-pressed={viewSide === 'before'}
                    onClick={() => setViewSide('before')}
                  >
                    Before
                  </button>
                  <button
                    type="button"
                    aria-pressed={viewSide === 'after'}
                    onClick={() => setViewSide('after')}
                  >
                    After
                  </button>
                </div>
              )}
              <div className={`org-compare-canvases ${compareNarrow ? 'is-narrow' : ''}`}>
                {(['before', 'after'] as const)
                  .filter((side) => !compareNarrow || side === viewSide)
                  .map((side) => (
                    <ReportingCanvas
                      key={side}
                      snapshot={view[side].snapshot}
                      selection={selection}
                      onSelect={(next) => {
                        setViewSide(side);
                        select(next);
                      }}
                      changes={changes}
                      search={search}
                      group={group}
                      label={`${side === 'before' ? 'Before' : 'After'} · v${view[side].revision}`}
                      viewport={sharedViewport}
                      onViewportChange={setSharedViewport}
                    />
                  ))}
              </div>
              <div className="org-change-list">
                <h3>
                  {changes.length
                    ? `${changes.length} structural ${changes.length === 1 ? 'change' : 'changes'}`
                    : 'No structural differences'}
                </h3>
                <div className="org-change-legend">
                  <span className="org-change-added">Added</span>
                  <span className="org-change-removed">Removed</span>
                  <span className="org-change-changed">Changed</span>
                </div>
                {changes.map((change) => (
                  <button
                    key={change.key}
                    type="button"
                    className="org-change-row"
                    onClick={() => {
                      setViewSide(change.status === 'removed' ? 'before' : 'after');
                      select({
                        kind: change.kind === 'membership' ? 'team' : change.kind,
                        id: change.id,
                      });
                    }}
                  >
                    <span className={`org-change-status org-change-${change.status}`}>
                      {change.status}
                    </span>
                    <strong>{change.name}</strong>
                    <small>{change.details.join(' · ') || prettyKind(change.kind)}</small>
                  </button>
                ))}
              </div>
            </>
          ) : viewMode === 'chart' ? (
            <>
              <ReportingCanvas
                key={view.mode === 'history' ? view.version.id : 'current'}
                snapshot={snapshot}
                selection={selection}
                onSelect={select}
                canEdit={canEdit && view.mode === 'current'}
                onQuickEdit={(roleId, mode) => {
                  if (canEdit && view.mode === 'current' && discard()) {
                    dirty.current = false;
                    setSelection(null);
                    setQuickEdit({ roleId, mode });
                  }
                }}
                search={search}
                group={group}
                viewport={view.mode === 'current' ? currentViewport.current : undefined}
                onViewportChange={
                  view.mode === 'current'
                    ? (viewport) => {
                        currentViewport.current = viewport;
                      }
                    : undefined
                }
              />
              {!snapshot.positions.length && canEdit && view.mode === 'current' && (
                <div className="org-empty-actions">
                  <button
                    type="button"
                    className="org-button org-button-primary"
                    onClick={() => select({ kind: 'position' })}
                  >
                    <Plus size={14} />
                    Add first role
                  </button>
                  <button
                    type="button"
                    className="org-button org-button-secondary"
                    onClick={() => setWizardOpen(true)}
                  >
                    <Sparkles size={14} />
                    Use guided setup
                  </button>
                </div>
              )}
            </>
          ) : (
            <RoleList
              snapshot={snapshot}
              search={search}
              group={group}
              selection={selection}
              onSelect={select}
            />
          )}
          {versionLoading && (
            <div className="org-version-loading" role="status">
              <span className="org-spinner" />
              Loading saved structure…
            </div>
          )}
        </div>
        {selection && snapshot && (
          <WorkbenchPanel
            title={
              selection.id
                ? selection.kind === 'position'
                  ? 'Role details'
                  : selection.kind === 'division'
                    ? 'Division details'
                    : 'Team details'
                : `Add ${selection.kind === 'position' ? 'role' : selection.kind}`
            }
            compact={compact}
            onClose={closeInspector}
          >
            <EntityInspector
              key={`${view.mode}:${view.mode === 'compare' ? viewSide : ''}:${selection.kind}:${selection.focus || ''}:${selection.id || `new-${selection.parentId || ''}-${selection.divisionId || ''}`}`}
              companyId={companyId}
              snapshot={snapshot}
              selection={selection}
              members={members}
              canEdit={canEdit}
              historical={view.mode !== 'current'}
              mutate={data.mutate}
              onSelect={select}
              onClose={closeInspector}
              onDirty={onDirty}
              onRefresh={() => void data.refresh()}
            />
          </WorkbenchPanel>
        )}
      </div>
      {quickEdit && current && (
        <RoleQuickEditor
          companyId={companyId}
          snapshot={current.snapshot}
          roleId={quickEdit.roleId}
          mode={quickEdit.mode}
          members={members}
          canEdit={canEdit && view.mode === 'current'}
          mutate={data.mutate}
          onClose={() => setQuickEdit(null)}
          onRefresh={data.refresh}
        />
      )}
      {wizardOpen && company && current && (
        <StructureWizard
          companyId={companyId}
          company={company}
          workspace={current}
          onApplied={(result) => {
            data.applyResult(result);
            setWizardOpen(false);
            setSelection(null);
          }}
          onRefresh={data.refresh}
          onClose={() => setWizardOpen(false)}
        />
      )}
    </div>
  );
}

const prettyKind = (kind: string) =>
  kind === 'position'
    ? 'Reporting role'
    : kind === 'membership'
      ? 'Team membership'
      : kind === 'division'
        ? 'Division'
        : 'Team';
function RoleList({
  snapshot,
  search,
  group,
  selection,
  onSelect,
}: {
  snapshot: OrganizationSnapshot;
  search: string;
  group: string;
  selection: EntitySelection | null;
  onSelect: (selection: EntitySelection) => void;
}) {
  const { visible } = visibleRoleIds(snapshot, search, group, new Set());
  const highlighted = selectedRoleIds(snapshot, selection);
  return (
    <div className="org-role-list">
      <table>
        <thead>
          <tr>
            <th>Role</th>
            <th>People</th>
            <th>Reports to</th>
            <th>Division / team</th>
          </tr>
        </thead>
        <tbody>
          {snapshot.positions
            .filter((p) => visible.has(p.id))
            .map((role) => (
              <tr key={role.id} className={highlighted.has(role.id) ? 'is-highlighted' : ''}>
                <td>
                  <button
                    type="button"
                    className="org-text-button"
                    onClick={() => onSelect({ kind: 'position', id: role.id })}
                  >
                    {role.title}
                  </button>
                  {isProposedRole(role) && <small>Proposed role</small>}
                </td>
                <td>
                  {role.assignments.length ? (
                    role.assignments.map((person) => (
                      <span key={person.id} className="org-list-person">
                        <span className="org-avatar">
                          {person.userPhoto ? (
                            <img src={person.userPhoto} alt="" />
                          ) : (
                            initials(person.userName)
                          )}
                        </span>
                        {person.userName}
                      </span>
                    ))
                  ) : (
                    <span className="org-muted">
                      {isProposedRole(role) ? 'Unassigned' : 'Vacant role'}
                    </span>
                  )}
                </td>
                <td>
                  {snapshot.positions.find((p) => p.id === role.parentPositionId)?.title ||
                    (role.parentPositionId ? 'Needs a reporting line' : 'Top level')}
                </td>
                <td>
                  {snapshot.divisions.find((d) => d.id === role.divisionId)?.name || '—'}
                  <small>{snapshot.teams.find((t) => t.id === role.teamId)?.name}</small>
                </td>
              </tr>
            ))}
        </tbody>
      </table>
      {!visible.size && (
        <div className="org-empty">
          <Network size={24} />
          <p>No matching roles</p>
        </div>
      )}
    </div>
  );
}
